import {
  ArrowBack,
  CheckCircle,
  Google,
  InfoOutlined,
  Login as LoginIcon,
  People,
  PersonAdd as PersonAddIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import Logo from './Logo';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  Typography,
} from '@mui/material';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { auth } from '../firebase';
import { colors } from '../theme';
import GoogleSignInCompleteModal from './GoogleSignInCompleteModal';
import TermsAcceptanceCheckbox from './TermsAcceptanceCheckbox';
import { TERMS_VERSION } from '../content/termsOfService';
import './Login.css';

interface LoginProps {
  userRole: 'dietitian' | 'client';
  isRegister?: boolean;
}

// Login form: input ve butonlar hepsi 48px sabit
const LOGIN_ROW_HEIGHT = 48;

// Ortak input stili: beyaz arka plan, nötr gri çerçeve, hover'da yazılar okunur.
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#ffffff',
    fontSize: '0.9375rem',
    fontWeight: 500,
    height: LOGIN_ROW_HEIGHT,
    minHeight: LOGIN_ROW_HEIGHT,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '& fieldset': {
      borderColor: '#dadce0',
      borderWidth: '1px',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    },
    '&:hover': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: colors.primary.main,
      },
      '& input': {
        color: '#1a2332',
        WebkitTextFillColor: '#1a2332',
      },
      '& input::placeholder': {
        color: '#8b95a0',
        opacity: 1,
        fontWeight: 400,
        WebkitTextFillColor: '#8b95a0',
      },
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary.main,
      borderWidth: '2px',
      boxShadow: `0 0 0 3px ${colors.primary.main}22`,
    },
    '& input': {
      color: '#1a2332',
      WebkitTextFillColor: '#1a2332',
      padding: '0 14px',
      height: '100%',
      boxSizing: 'border-box',
      '&::placeholder': {
        color: '#8b95a0',
        opacity: 1,
        fontWeight: 400,
        WebkitTextFillColor: '#8b95a0',
      },
    },
  },
  '& .MuiInputLabel-outlined': {
    color: '#5c6b7a',
    fontWeight: 500,
    '&.Mui-focused': {
      color: colors.primary.main,
    },
  },
  '&:hover .MuiInputLabel-outlined': {
    color: '#5c6b7a',
  },
  '&:hover .MuiInputLabel-outlined.Mui-focused': {
    color: colors.primary.main,
  },
};

const Login: React.FC<LoginProps> = ({ userRole, isRegister = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = new URLSearchParams(location.search).get('returnUrl');
  const postLoginPath = returnUrl && returnUrl.startsWith('/') ? returnUrl : (userRole === 'dietitian' ? '/dietitian' : '/client');
  const { showSuccess, showError } = useToast();
  const { loadDietitian, loadClient } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [acceptsOnlineClients, setAcceptsOnlineClients] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSignUp, setIsSignUp] = useState(isRegister);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showGoogleCompleteModal, setShowGoogleCompleteModal] = useState(false);
  const [googleUserData, setGoogleUserData] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    user: any;
  } | null>(null);

  const googleProvider = new GoogleAuthProvider();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Önceki kullanıcının verilerini temizle (farklı kullanıcı giriş yapıyorsa)
    const previousEmail = localStorage.getItem('userEmail');
    if (previousEmail && previousEmail !== email) {
      console.log('🔄 Farklı kullanıcı giriş yapıyor, eski veriler temizleniyor...');
      // Tüm kullanıcıya özel localStorage verilerini temizle
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userRole');
      localStorage.removeItem('dietitianData');
      localStorage.removeItem('clientData');
      localStorage.removeItem('client-onboarding-completed');

      // TaroBot mesajlarını temizle
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('taro-messages-')) {
          localStorage.removeItem(key);
        }
        // Bildirimleri temizle
        if (key.startsWith('notifications_') || key.startsWith('processedMessages_')) {
          localStorage.removeItem(key);
        }
      });
    }

    // Kayıt için validasyon
    if (isSignUp && userRole === 'dietitian') {
      if (!firstName.trim() || !lastName.trim()) {
        setError('Lütfen adınızı ve soyadınızı giriniz.');
        showError('Lütfen adınızı ve soyadınızı giriniz.', 'Eksik Bilgi');
        setLoading(false);
        return;
      }
      if (!termsAccepted) {
        setTermsError(true);
        showError('Devam etmek için kullanım şartlarını kabul etmelisiniz.', 'Kullanım Şartları');
        setLoading(false);
        return;
      }
      setTermsError(false);
    }

    try {
      if (isSignUp) {
        // ADIM 1: Firebase'de kullanıcı oluştur
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        console.log('✅ Firebase kullanıcısı oluşturuldu:', email);

        // ADIM 2: Backend'de profil oluştur
        try {
          if (userRole === 'dietitian') {
            const { dietitianApi } = await import('../services/dietitianApi');
            await dietitianApi.createDietitian({
              email: email,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              acceptsOnlineClients: acceptsOnlineClients,
              termsAcceptedAt: new Date().toISOString(),
              termsVersion: TERMS_VERSION,
            });
            console.log('✅ Backend dietitian profili oluşturuldu');

            // Custom claim ekle (role bilgisi)
            localStorage.setItem('userRole', 'dietitian');
          }

          showSuccess('Hesabınız başarıyla oluşturuldu!', 'Kayıt Başarılı');
        } catch (backendError: any) {
          // Backend'de hata oldu - Firebase kullanıcısını sil
          console.error('❌ Backend profil oluşturma hatası:', backendError);

          try {
            // Firebase kullanıcısını sil
            await userCredential.user.delete();
            console.log('🗑️ Firebase kullanıcısı silindi');
          } catch (deleteError) {
            console.error('Firebase kullanıcı silme hatası:', deleteError);
          }

          throw new Error(
            'Profil oluşturulurken bir hata oluştu. Lütfen tekrar deneyin. ' +
            `(Detay: ${backendError.message || 'Backend hatası'})`
          );
        }
      } else {
        // Giriş işlemi
        await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ Firebase girişi başarılı:', email);

        // Kullanıcının backend'de kaydı var mı kontrol et
        try {
          if (userRole === 'dietitian') {
            // ÖNCE: Bu email'in client olup olmadığını kontrol et
            const { clientApi } = await import('../services/clientApi');
            try {
              await clientApi.getClientByEmail(email);
              // Bu email bir client - diyetisyen panelinden giriş yapamaz
              await auth.signOut();
              throw new Error(
                'Bu e-posta adresi bir danışan hesabına ait. Lütfen danışan panelinden giriş yapın.'
              );
            } catch (clientCheckError: any) {
              // Client değil veya bulunamadı - devam et
              if (clientCheckError.response?.status === 404 || clientCheckError.message?.includes('404')) {
                // Client değil, diyetisyen kontrolüne devam et
                console.log('✅ Email client değil, diyetisyen kontrolü yapılıyor...');
              } else if (clientCheckError.message?.includes('danışan panelinden')) {
                // Client hatası - yukarı fırlat
                await auth.signOut();
                throw clientCheckError;
              }
            }

            const { dietitianApi } = await import('../services/dietitianApi');

            try {
              await dietitianApi.getDietitianByEmail(email);
              console.log('✅ Backend dietitian kaydı bulundu');
            } catch (notFoundError: any) {
              // Backend'de kayıt yok - otomatik oluştur
              if (notFoundError.response?.status === 404 || notFoundError.message?.includes('404')) {
                console.log('⚠️ Backend kaydı bulunamadı, oluşturuluyor...');

                try {
                  // Otomatik profil oluşturma - isim soyisim boş olacak
                  await dietitianApi.createDietitian({
                    email: email,
                    firstName: 'Profil',
                    lastName: 'Tamamlanmamış',
                  });
                  console.log(
                    '✅ Backend dietitian profili otomatik oluşturuldu'
                  );
                  showSuccess(
                    'Profiliniz oluşturuldu. Hoş geldiniz!',
                    'Giriş Başarılı'
                  );
                } catch (createError) {
                  console.error(
                    '❌ Otomatik profil oluşturma hatası:',
                    createError
                  );
                  throw new Error(
                    'Profil oluşturulamadı. Lütfen yöneticinize başvurun.'
                  );
                }
              } else {
                throw notFoundError;
              }
            }
          } else {
            // Client kontrolü
            // ÖNCE: Bu email'in diyetisyen olup olmadığını kontrol et
            const { dietitianApi } = await import('../services/dietitianApi');
            try {
              await dietitianApi.getDietitianByEmail(email);
              // Bu email bir diyetisyen - client panelinden giriş yapamaz
              await auth.signOut();
              throw new Error(
                'Bu e-posta adresi bir diyetisyen hesabına ait. Lütfen diyetisyen panelinden giriş yapın.'
              );
            } catch (dietitianCheckError: any) {
              // Diyetisyen değil veya bulunamadı - devam et
              if (dietitianCheckError.response?.status === 404 || dietitianCheckError.message?.includes('404')) {
                // Diyetisyen değil, client kontrolüne devam et
                console.log('✅ Email diyetisyen değil, client kontrolü yapılıyor...');
              } else if (dietitianCheckError.message?.includes('diyetisyen panelinden')) {
                // Diyetisyen hatası - yukarı fırlat
                await auth.signOut();
                throw dietitianCheckError;
              }
            }

            // Client kontrolü
            const { clientApi } = await import('../services/clientApi');
            try {
              await clientApi.getClientByEmail(email);
              console.log('✅ Backend client kaydı bulundu');
            } catch (notFoundError: any) {
              // Client backend'de bulunamadı - otomatik oluştur (Google ile giriş yapıyorsa)
              if (notFoundError.response?.status === 404 || notFoundError.message?.includes('404')) {
                console.log('⚠️ Backend client kaydı bulunamadı, otomatik oluşturuluyor...');

                try {
                  // Google'dan gelen isim bilgisi varsa kullan, yoksa varsayılan
                  await clientApi.createClient({
                    firstName: firstName || 'Kullanıcı',
                    lastName: lastName || 'Adı',
                    email: email,
                    isSelfRegistered: true,
                  });
                  console.log('✅ Backend client profili otomatik oluşturuldu');
                  showSuccess(
                    'Profiliniz oluşturuldu. Hoş geldiniz!',
                    'Giriş Başarılı'
                  );
                } catch (createError: any) {
                  console.error('❌ Otomatik profil oluşturma hatası:', createError);
                  await auth.signOut();
                  throw new Error(
                    'Profil oluşturulamadı. Lütfen yöneticinize başvurun.'
                  );
                }
              } else {
                await auth.signOut();
                throw notFoundError;
              }
            }
          }
        } catch (apiError: any) {
          // API hatası
          await auth.signOut();
          console.error('❌ API hatası:', apiError);

          if (apiError.message.includes('panelinden giriş yapın')) {
            throw apiError; // Yanlış panel hatası
          }

          throw new Error(
            apiError.message || 'Giriş yapılırken bir hata oluştu.'
          );
        }

        showSuccess('Giriş başarılı! Yönlendiriliyorsunuz...', 'Hoş Geldiniz');
      }

      localStorage.setItem('userEmail', email);
      localStorage.setItem('userRole', userRole);
      console.log('Email kaydedildi:', email);
      console.log('Role kaydedildi:', userRole);

      // Login olduktan sonra dietitian/client verisini Context'e yükle
      try {
        if (userRole === 'dietitian') {
          await loadDietitian(email);
        } else {
          await loadClient(email);
        }
      } catch (error) {
        console.error('Veri yükleme hatası (kritik değil):', error);
        // Hata olsa bile devam et, hook'larda tekrar çekilecek
      }

      setTimeout(() => {
        navigate(postLoginPath);
      }, 1000);
    } catch (error: any) {
      console.error('❌ Hata:', error);

      let errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';

      // Firebase Authentication hataları
      if (error.code === 'auth/invalid-email') {
        errorMessage = 'Geçersiz e-posta adresi.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'Bu hesap devre dışı bırakılmış.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'Bu e-posta ile kayıtlı kullanıcı bulunamadı.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Hatalı şifre.';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage =
          'Bu e-posta adresi zaten kullanılıyor. Giriş yapmayı deneyin.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Şifre en az 6 karakter olmalıdır.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'E-posta veya şifre hatalı.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'E-posta/şifre girişi etkinleştirilmemiş.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'İnternet bağlantınızı kontrol edin.';
      }
      // Custom error messages (backend hatalarından gelen)
      else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      showError(errorMessage, isSignUp ? 'Kayıt Hatası' : 'Giriş Hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');

    try {
      // Önceki kullanıcının verilerini temizle
      const previousEmail = localStorage.getItem('userEmail');
      if (previousEmail) {
        console.log('🔄 Farklı kullanıcı giriş yapıyor, eski veriler temizleniyor...');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
        localStorage.removeItem('dietitianData');
        localStorage.removeItem('clientData');
        localStorage.removeItem('client-onboarding-completed');

        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('taro-messages-')) {
            localStorage.removeItem(key);
          }
          if (key.startsWith('notifications_') || key.startsWith('processedMessages_')) {
            localStorage.removeItem(key);
          }
        });
      }

      // Google ile giriş yap
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const email = user.email;
      const displayName = user.displayName || '';

      if (!email) {
        throw new Error('Google hesabınızdan e-posta adresi alınamadı.');
      }

      console.log('✅ Google girişi başarılı:', email);

      // Ad ve soyadı ayır (Google'dan gelen displayName'den)
      let firstName = '';
      let lastName = '';
      if (displayName) {
        const nameParts = displayName.trim().split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      // Kullanıcının backend'de kaydı var mı kontrol et
      try {
        if (userRole === 'dietitian') {
          // ÖNCE: Bu email'in client olup olmadığını kontrol et
          const { clientApi } = await import('../services/clientApi');
          try {
            await clientApi.getClientByEmail(email);
            // Bu email bir client - diyetisyen panelinden giriş yapamaz
            await auth.signOut();
            throw new Error(
              'Bu e-posta adresi bir danışan hesabına ait. Lütfen danışan panelinden giriş yapın.'
            );
          } catch (clientCheckError: any) {
            // Client değil veya bulunamadı - devam et
            if (clientCheckError.response?.status === 404 || clientCheckError.message?.includes('404')) {
              // Client değil, diyetisyen kontrolüne devam et
              console.log('✅ Email client değil, diyetisyen kontrolü yapılıyor...');
            } else if (clientCheckError.message?.includes('danışan panelinden')) {
              // Client hatası - yukarı fırlat
              await auth.signOut();
              throw clientCheckError;
            }
          }

          const { dietitianApi } = await import('../services/dietitianApi');

          try {
            await dietitianApi.getDietitianByEmail(email);
            console.log('✅ Backend dietitian kaydı bulundu');
            // Mevcut kullanıcı - direkt giriş yap
            showSuccess('Google ile giriş başarılı! Yönlendiriliyorsunuz...', 'Hoş Geldiniz');

            localStorage.setItem('userEmail', email);
            localStorage.setItem('userRole', userRole);

            // Login olduktan sonra dietitian verisini Context'e yükle
            try {
              await loadDietitian(email);
            } catch (error) {
              console.error('Veri yükleme hatası (kritik değil):', error);
            }

            setTimeout(() => {
              navigate(postLoginPath);
            }, 1000);
            setGoogleLoading(false);
            return;
          } catch (notFoundError: any) {
            // Backend'de kayıt yok - form göster
            if (notFoundError.response?.status === 404 || notFoundError.message?.includes('404')) {
              console.log('⚠️ Backend kaydı bulunamadı, ek bilgiler isteniyor...');
              setGoogleUserData({
                email,
                firstName: firstName || '',
                lastName: lastName || '',
                user,
              });
              setShowGoogleCompleteModal(true);
              setGoogleLoading(false);
              return; // Modal açılacak, burada dur
            } else {
              throw notFoundError;
            }
          }
        } else {
          // ÖNCE: Bu email'in diyetisyen olup olmadığını kontrol et
          const { dietitianApi } = await import('../services/dietitianApi');
          try {
            await dietitianApi.getDietitianByEmail(email);
            // Bu email bir diyetisyen - client panelinden giriş yapamaz
            await auth.signOut();
            throw new Error(
              'Bu e-posta adresi bir diyetisyen hesabına ait. Lütfen diyetisyen panelinden giriş yapın.'
            );
          } catch (dietitianCheckError: any) {
            // Diyetisyen değil veya bulunamadı - devam et
            if (dietitianCheckError.response?.status === 404 || dietitianCheckError.message?.includes('404')) {
              // Diyetisyen değil, client kontrolüne devam et
              console.log('✅ Email diyetisyen değil, client kontrolü yapılıyor...');
            } else if (dietitianCheckError.message?.includes('diyetisyen panelinden')) {
              // Diyetisyen hatası - yukarı fırlat
              await auth.signOut();
              throw dietitianCheckError;
            }
          }

          // Client kontrolü
          const { clientApi } = await import('../services/clientApi');
          try {
            await clientApi.getClientByEmail(email);
            console.log('✅ Backend client kaydı bulundu');
            // Mevcut kullanıcı - direkt giriş yap
            showSuccess('Google ile giriş başarılı! Yönlendiriliyorsunuz...', 'Hoş Geldiniz');

            localStorage.setItem('userEmail', email);
            localStorage.setItem('userRole', userRole);

            // Login olduktan sonra client verisini Context'e yükle
            try {
              await loadClient(email);
            } catch (error) {
              console.error('Veri yükleme hatası (kritik değil):', error);
            }

            setTimeout(() => {
              navigate(postLoginPath);
            }, 1000);
            setGoogleLoading(false);
            return;
          } catch (notFoundError: any) {
            // Client backend'de bulunamadı - form göster
            if (notFoundError.response?.status === 404 || notFoundError.message?.includes('404')) {
              console.log('⚠️ Backend client kaydı bulunamadı, ek bilgiler isteniyor...');
              setGoogleUserData({
                email,
                firstName: firstName || '',
                lastName: lastName || '',
                user,
              });
              setShowGoogleCompleteModal(true);
              setGoogleLoading(false);
              return; // Modal açılacak, burada dur
            } else {
              await auth.signOut();
              throw notFoundError;
            }
          }
        }
      } catch (apiError: any) {
        // API hatası
        await auth.signOut();
        console.error('❌ API hatası:', apiError);

        if (apiError.message.includes('panelinden giriş yapın')) {
          throw apiError; // Yanlış panel hatası
        }

        throw new Error(
          apiError.message || 'Giriş yapılırken bir hata oluştu.'
        );
      }
    } catch (error: any) {
      console.error('❌ Google giriş hatası:', error);

      let errorMessage = 'Google ile giriş yapılırken bir hata oluştu.';

      // Firebase Authentication hataları
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Giriş penceresi kapatıldı.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Giriş penceresi engellendi. Lütfen popup engelleyicisini kapatın.';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'Bu e-posta adresi ile farklı bir giriş yöntemi kullanılmış. Lütfen e-posta/şifre ile giriş yapın.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'İnternet bağlantınızı kontrol edin.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      showError(errorMessage, 'Google Giriş Hatası');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleCompleteModalSubmit = async (data: {
    firstName: string;
    lastName: string;
    phone?: string;
    age?: number;
    weight?: number;
    height?: number;
    gender?: string;
    acceptsOnlineClients?: boolean;
  }) => {
    if (!googleUserData) return;

    setGoogleLoading(true);
    try {
      if (userRole === 'dietitian') {
        const { dietitianApi } = await import('../services/dietitianApi');
        await dietitianApi.createDietitian({
          email: googleUserData.email,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          acceptsOnlineClients: data.acceptsOnlineClients || false,
          termsAcceptedAt: new Date().toISOString(),
          termsVersion: TERMS_VERSION,
        });
        console.log('✅ Backend dietitian profili oluşturuldu');
        showSuccess('Hesabınız başarıyla oluşturuldu!', 'Kayıt Başarılı');
      } else {
        const { clientApi } = await import('../services/clientApi');
        await clientApi.createClient({
          firstName: data.firstName,
          lastName: data.lastName,
          email: googleUserData.email,
          phone: data.phone,
          age: data.age,
          weight: data.weight,
          height: data.height,
          gender: data.gender,
          isSelfRegistered: true,
          termsAcceptedAt: new Date().toISOString(),
          termsVersion: TERMS_VERSION,
        });
        console.log('✅ Backend client profili oluşturuldu');
        showSuccess('Hesabınız başarıyla oluşturuldu!', 'Kayıt Başarılı');
      }

      localStorage.setItem('userEmail', googleUserData.email);
      localStorage.setItem('userRole', userRole);

      // Login olduktan sonra dietitian/client verisini Context'e yükle
      try {
        if (userRole === 'dietitian') {
          await loadDietitian(googleUserData.email);
        } else {
          await loadClient(googleUserData.email);
        }
      } catch (error) {
        console.error('Veri yükleme hatası (kritik değil):', error);
      }

      setShowGoogleCompleteModal(false);
      setGoogleUserData(null);

      setTimeout(() => {
        navigate(postLoginPath);
      }, 1000);
    } catch (error: any) {
      console.error('❌ Profil oluşturma hatası:', error);
      throw error;
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleCompleteModalCancel = async () => {
    if (googleUserData?.user) {
      try {
        await googleUserData.user.delete();
        console.log('🗑️ Firebase kullanıcısı silindi');
      } catch (deleteError) {
        console.error('Firebase kullanıcı silme hatası:', deleteError);
      }
    }
    await auth.signOut();
    setShowGoogleCompleteModal(false);
    setGoogleUserData(null);
    setGoogleLoading(false);
  };

  return (
    <div className="login-container">
      {/* Geri Butonu */}
      <IconButton
        onClick={() => navigate('/')}
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          width: '48px',
          height: '48px',
          zIndex: 1000,
          bgcolor: 'rgba(255, 255, 255, 0.85) ',
          backdropFilter: 'blur(30px)',
          color: 'rgba(0, 0, 0, 0.6)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            color: 'rgba(0, 0, 0, 0.8)',
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <ArrowBack />
      </IconButton>

      {/* Decorative Background Circles */}
      <div className="decorative-circles">
        <div className="circle circle-1"></div>
        <div className="circle circle-2"></div>
        <div className="circle circle-3"></div>
      </div>

      {/* Main Content - Center Layout */}
      <div className="login-right">
        {/* Features Card */}
        <div className="features-card">
          <div className="brand-section-inline">
            <Box sx={{ flexShrink: 0 }}>
              <Logo size={60} animate />
            </Box>
            <div>
              <h1 className="brand-title-inline">Dietaro</h1>
              <p className="brand-subtitle-inline">
                Profesyonel Diyetisyen Platformu
              </p>
            </div>
          </div>

          <div className="features-list-inline">
            <div className="feature-item-inline">
              <CheckCircle sx={{ color: colors.primary.main, fontSize: 28 }} />
              <div>
                <h3>Danışan Yönetimi</h3>
                <p>Tüm danışanlarınızı tek panelden yönetin</p>
              </div>
            </div>
            <div className="feature-item-inline">
              <CheckCircle sx={{ color: colors.primary.main, fontSize: 28 }} />
              <div>
                <h3>Online Randevu</h3>
                <p>Randevularınızı otomatik planlayın</p>
              </div>
            </div>
            <div className="feature-item-inline">
              <CheckCircle sx={{ color: colors.primary.main, fontSize: 28 }} />
              <div>
                <h3>İlerleme Takibi</h3>
                <p>Detaylı raporlama ve analiz</p>
              </div>
            </div>
            <div className="feature-item-inline">
              <CheckCircle sx={{ color: colors.primary.main, fontSize: 28 }} />
              <div>
                <h3>Anlık İletişim</h3>
                <p>Güvenli mesajlaşma sistemi</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="login-form-container">
          <div className="login-form-header">
            <div className="form-icon">
              {isSignUp ? <PersonAddIcon /> : <LoginIcon />}
            </div>
            <h2 className="form-title">
              {userRole === 'dietitian'
                ? isSignUp
                  ? 'Profesyonel Hesap Oluşturun'
                  : 'Tekrar Hoş Geldiniz'
                : 'Danışan Paneli'}
            </h2>
            <p className="form-subtitle">
              {userRole === 'dietitian'
                ? isSignUp
                  ? 'Danışan yönetiminizi dijitalleştirin'
                  : 'Profesyonel panelinize giriş yapın'
                : 'Danışan panelinize giriş yapın'}
            </p>
          </div>

          <Box component="form" onSubmit={handleAuth} className="login-form">
            {/* Ad ve Soyad - Sadece Kayıt Formunda Göster */}
            {isSignUp && userRole === 'dietitian' && (
              <>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="firstName"
                  label="Adınız"
                  name="firstName"
                  autoComplete="given-name"
                  autoFocus
                  value={firstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFirstName(e.target.value)
                  }
                  placeholder="Ahmet"
                  sx={inputStyles}
                />

                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="lastName"
                  label="Soyadınız"
                  name="lastName"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setLastName(e.target.value)
                  }
                  placeholder="Yılmaz"
                  sx={inputStyles}
                />
              </>
            )}

            <TextField
              margin="dense"
              required
              fullWidth
              id="email"
              label="E-posta Adresi"
              name="email"
              autoComplete="email"
              autoFocus={!isSignUp || userRole !== 'dietitian'}
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
              placeholder="dyt.isminiz@email.com"
              sx={inputStyles}
            />

            <TextField
              margin="dense"
              required
              fullWidth
              name="password"
              label="Şifre"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      sx={{ color: '#5c6b7a', '&:hover': { color: colors.primary.main } }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputStyles}
            />

            {/* Online danışan seçeneği - sadece diyetisyen kayıt ekranında, mail ve şifrenin altında */}
            {isSignUp && userRole === 'dietitian' && (
              <Box
                sx={{
                  mt: 2,
                  mb: 0.5,
                  p: 2,
                  borderRadius: 2.5,
                  background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.06) 0%, rgba(46, 125, 50, 0.04) 100%)',
                  border: `1.5px solid ${acceptsOnlineClients ? colors.primary.main : 'rgba(67, 160, 71, 0.2)'}`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    borderColor: colors.primary.main,
                    background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.1) 0%, rgba(46, 125, 50, 0.06) 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${colors.primary.main}15`,
                  },
                }}
                onClick={() => setAcceptsOnlineClients(!acceptsOnlineClients)}
              >
                {/* Üst satır: ikon + checkbox + başlık */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      background: acceptsOnlineClients
                        ? colors.primary.main
                        : 'rgba(67, 160, 71, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      flexShrink: 0,
                    }}
                  >
                    <People
                      sx={{
                        color: acceptsOnlineClients ? 'white' : colors.primary.main,
                        fontSize: 22,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={acceptsOnlineClients}
                          onChange={(e) =>
                            setAcceptsOnlineClients(e.target.checked)
                          }
                          sx={{
                            color: colors.primary.main,
                            '&.Mui-checked': {
                              color: colors.primary.main,
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 24,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{
                            color: '#1a2332',
                            fontSize: '0.9375rem',
                          }}
                        >
                          Online danışan almak istiyorum
                        </Typography>
                      }
                      sx={{ m: 0, mr: 0 }}
                    />
                  </Box>
                </Box>
                {/* Alt satır: açıklama metni tam genişlikte */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75,
                    mt: 1.25,
                    width: '100%',
                  }}
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      color: '#5c6b7a',
                      mt: 0.25,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#5c6b7a',
                      fontSize: '0.8125rem',
                      lineHeight: 1.6,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    Bu seçeneği işaretlerseniz, danışanlar sizi platformda
                    bulabilecek, seçebilecek ve doğrudan iletişime geçebilecekler.
                    Profiliniz diyetisyen arama sayfasında görünecektir.
                  </Typography>
                </Box>
              </Box>
            )}

            {isSignUp && userRole === 'dietitian' && (
              <TermsAcceptanceCheckbox
                checked={termsAccepted}
                onChange={(v) => { setTermsAccepted(v); setTermsError(false); }}
                error={termsError}
                disabled={loading}
              />
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  mt: 1.5,
                  borderRadius: 2,
                }}
              >
                {error}
              </Alert>
            )}

            {/* Şifremi Unuttum - hover saydam, renk aynı, altyazı yok */}
            {!isSignUp && (
              <Box sx={{ mt: 0.5, mb: 0, textAlign: 'right' }}>
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate(`/${userRole}/forgot-password`)}
                  sx={{
                    display: 'inline-block',
                    color: colors.primary.main,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'text-decoration 0.2s ease',
                    '&:hover': {
                      color: colors.primary.main,
                      background: 'transparent',
                      textDecoration: 'underline',
                    },
                    '&:active': {
                      color: colors.primary.main,
                      background: 'transparent',
                    },
                  }}
                >
                  Şifremi Unuttum
                </MuiLink>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={isSignUp ? <PersonAddIcon /> : <LoginIcon />}
              sx={{
                mt: !isSignUp ? 1.25 : 2,
                mb: 1,
                height: LOGIN_ROW_HEIGHT,
                minHeight: LOGIN_ROW_HEIGHT,
                borderRadius: 2,
                background: colors.gradients.primary,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryMedium,
                  boxShadow: colors.shadows.cardHover,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loading
                ? isSignUp
                  ? 'Hesap Oluşturuluyor...'
                  : 'Giriş Yapılıyor...'
                : isSignUp
                  ? 'Profesyonel Hesap Oluştur'
                  : 'Panele Giriş Yap'}
            </Button>

            {/* Ayırıcı */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                my: 1.5,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  mx: 1.5,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                veya
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                }}
              />
            </Box>

            {/* Google ile Giriş Butonu - 48px */}
            <Box sx={{ mb: 1 }}>
              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={loading || googleLoading}
                startIcon={<Google />}
                sx={{
                  height: LOGIN_ROW_HEIGHT,
                  minHeight: LOGIN_ROW_HEIGHT,
                  borderRadius: 2,
                  borderColor: '#db4437',
                  color: '#db4437',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#c23321',
                    backgroundColor: 'rgba(219, 68, 55, 0.08)',
                    borderWidth: 2,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {googleLoading
                  ? 'Google ile giriş yapılıyor...'
                  : 'Google ile Giriş Yap'}
              </Button>
            </Box>

            {/* Kayıt/Giriş Değiştirme - Sadece Dietitian için */}
            {userRole === 'dietitian' && (
              <div className="form-footer">
                <Typography variant="body2" color="text.secondary">
                  {isSignUp
                    ? 'Zaten hesabınız var mı?'
                    : 'Henüz üye değil misiniz?'}
                </Typography>
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setIsSignUp(!isSignUp)}
                  sx={{
                    ml: 1,
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    width: 'auto',
                    '&:hover': {
                      color: colors.primary.dark,
                      backgroundColor: 'rgba(67, 160, 71, 0.08)',
                      textDecoration: 'none',
                    },
                    '&:active': {
                      backgroundColor: 'rgba(67, 160, 71, 0.12)',
                    },
                  }}
                >
                  {isSignUp ? 'Giriş Yapın' : 'Ücretsiz Başlayın'}
                </MuiLink>
              </div>
            )}

            {/* Client için bilgilendirme mesajı */}
            {userRole === 'client' && !isSignUp && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Hesabınız diyetisyeniniz tarafından oluşturulmuş olabilir veya
                  kendiniz kayıt olmuş olabilirsiniz.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Henüz hesabınız yok mu?{' '}
                  <MuiLink
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate('/client/register')}
                    sx={{
                      color: colors.primary.main,
                      fontWeight: 600,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      background: 'transparent',
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: 1,
                      transition: 'all 0.2s ease',
                      width: 'auto',
                      '&:hover': {
                        color: colors.primary.dark,
                        backgroundColor: 'rgba(67, 160, 71, 0.08)',
                        textDecoration: 'none',
                      },
                      '&:active': {
                        backgroundColor: 'rgba(67, 160, 71, 0.12)',
                      },
                    }}
                  >
                    Kayıt Olun
                  </MuiLink>
                </Typography>
              </Box>
            )}
          </Box>
        </div>
      </div>

      {/* Google Sign-In Complete Modal */}
      {googleUserData && (
        <GoogleSignInCompleteModal
          open={showGoogleCompleteModal}
          userRole={userRole}
          email={googleUserData.email}
          firstName={googleUserData.firstName}
          lastName={googleUserData.lastName}
          onComplete={handleGoogleCompleteModalSubmit}
          onCancel={handleGoogleCompleteModalCancel}
          loading={googleLoading}
        />
      )}
    </div>
  );
};

export default Login;
